/**
 * Pure helpers + types used across the stock-detail panel.
 * No React, no DOM — formatting, statistics, and price-range filtering only.
 */

export interface PricePoint {
  date: string
  close: number
}

export interface ProfileData {
  description?: string
  ceo?: string
  industry?: string
  fullTimeEmployees?: string
  website?: string
  country?: string
  city?: string
  state?: string
  fyEndMonth?: number
  fyEndMonthName?: string
  latestReportedPeriod?: string
  latestReportedDate?: string
  nextReportDate?: string
  nextReportDaysOut?: number
}

export type PriceRange = '3M' | '6M' | 'YTD' | '1Y' | '5Y' | 'Max'
export const RANGES: PriceRange[] = ['3M', '6M', 'YTD', '1Y', '5Y', 'Max']

// ─── Formatting ─────────────────────────────────────────────────────────────

export function fmtMkt(v: number): string {
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`
  return `$${(v / 1e6).toFixed(0)}M`
}

export function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '--'
  const pct = v * 100
  // Adaptive precision: small yields (|x|<1%) need 2 decimals so a 0.46%
  // dividend doesn't round to "0.5%" (and the previous default .toFixed(2)
  // on the raw decimal rendered 0.0046 as "0.00", which read as zero
  // — the bug Steve called out 2026-05-22). Larger values use 1 decimal
  // (25.71% is overprecise; 25.7% reads cleanly).
  const decimals = Math.abs(pct) < 1 ? 2 : 1
  return pct.toFixed(decimals) + '%'
}

// 2026-05-22 comprehensive sweep (Steve directive: "every metric noted in %
// should be listed as a percent — ie 82, not 0.82"). Each entry here is a
// factor stored as a decimal that semantically represents a percentage
// rate. fmtVal() routes these through fmtPct which multiplies by 100 and
// appends '%'. Tightly maintained — adding a new factor that doesn't
// belong here will render the raw decimal (last week's 0.46% → "0.00" bug).
const PCT_KEYS = new Set([
  // Profitability — margins + capital efficiency (all rates 0–~1)
  'roe', 'roa', 'roic', 'rotce',
  'fwdRoe', 'fwdRoa', 'fwdNetMargin',
  'grossMargin', 'netMargin',
  'cfoToAssets', 'accrualsRatio', 'assetGrowth', 'grossProfitToAssets',
  // Profitability — financial / bank
  'nim', 'nimT12', 'efficiencyRatio', 'provisionsToNII',
  // Growth — forward
  'fwdEpsGrowth', 'fwdRevGrowth', 'fwdEpsCagr2yr', 'fwdRevCagr2yr',
  'fwdEpsCagr3yr', 'fwdOperLeverage', 'nextQtrEpsGrowth',
  // Growth — trailing + revision velocity family
  'epsCAGR3yr', 'fcfGrowth3yr', 'revenueGrowth3yr',
  'grossMarginExpansion3yr', 'opMarginExpansion3yr',
  'epsRevision', 'earningsSurprise',
  'revisionDelta7d', 'revisionDelta30d', 'revisionDelta90d', 'revisionAcceleration',
  // Growth / innovation
  'rdIntensity',
  // Safety — non-financial
  'equityToAssets', 'retainedEarningsToAssets',
  // Safety — bank-specific (regulator ratios)
  'tier1CapRatio', 'cet1Ratio', 'tceRatio', 'nplRatio', 'provCoverage', 'ncoRate', 'loanToDeposit',
  // Safety / Profitability — REIT-specific
  'occupancyRate', 'sameStoreNoiGrowth', 'ffoYield',
  // Shareholder Returns — payout yields
  'dividendYield', 'buybackYield', 'fwdDividendYield', 'impliedBuybackYield',
  'netDebtPaydownYield', 'sbcDilution',
  // Momentum
  'insiderNetBuy', 'momentum', 'nearHigh', 'shortTermReversal',
])

// Factors expressed as multiples ("x"). EV/EBITDA = 12.5x, P/E = 25x.
// grossProfitToAssets + cfoToAssets moved OUT of here to PCT_KEYS — they
// are margin-flavored balance-sheet ratios that conventionally read as %.
const RATIO_KEYS = new Set([
  'debtToEbitda', 'currentRatio', 'fcfToDebt',
  'fwdPE', 'fwdPE2yr', 'fwdPE3yr', 'fwdEvToRevenue', 'fwdEvToEbitda',
  'pegRatio', 'evToEbitda', 'pToFcf', 'evToGrossProfit',
  'interestCoverage', 'priceToBook', 'pToRevenue',
  'fwdPtoBook', 'fwdPtoSales',
  'pToFfo', 'pToNav', 'fwdDebtToEbitda',
])

export function fmtVal(v: number | null | undefined, key: string): string {
  if (v === null || v === undefined) return 'N/A'
  if (PCT_KEYS.has(key)) return fmtPct(v)
  if (key === 'piotroski') return `${v}/9`
  if (RATIO_KEYS.has(key)) return v.toFixed(1) + 'x'
  if (key === 'beta') return v.toFixed(2)
  if (key === 'altmanZ') return v.toFixed(1)
  if (key === 'epsVariability') return v.toFixed(2)
  return v.toFixed(2)
}

// ─── Statistics ─────────────────────────────────────────────────────────────

export function computeStats(values: (number | null)[]): { mean: number; std: number } | null {
  const valid = values.filter((v): v is number => v !== null && isFinite(v))
  if (valid.length < 5) return null
  const mean = valid.reduce((a, b) => a + b, 0) / valid.length
  const std = Math.sqrt(valid.reduce((a, b) => a + (b - mean) ** 2, 0) / valid.length)
  return { mean, std: std || 1 }
}

/**
 * What percentage of valid values lie below the input value? Returns 0-100, or
 * null if the input is null. `invert=true` flips the comparison (used for
 * factors where lower is better, like P/E).
 */
export function computePercentile(
  value: number | null,
  allValues: (number | null)[],
  invert: boolean,
): number | null {
  if (value === null) return null
  const valid = allValues.filter((v): v is number => v !== null && isFinite(v))
  if (valid.length === 0) return null
  const below = valid.filter(v => (invert ? v > value : v < value)).length
  return Math.round((below / valid.length) * 100)
}

// ─── Price range filter ─────────────────────────────────────────────────────

export function filterPricesByRange(
  prices: PricePoint[],
  range: PriceRange,
  now: Date = new Date(),
): PricePoint[] {
  if (prices.length === 0) return prices
  if (range === 'Max') return prices

  let cutoff: Date
  switch (range) {
    case '3M':
      cutoff = new Date(now)
      cutoff.setMonth(cutoff.getMonth() - 3)
      break
    case '6M':
      cutoff = new Date(now)
      cutoff.setMonth(cutoff.getMonth() - 6)
      break
    case 'YTD':
      cutoff = new Date(now.getFullYear(), 0, 1)
      break
    case '1Y':
      cutoff = new Date(now)
      cutoff.setFullYear(cutoff.getFullYear() - 1)
      break
    case '5Y':
      cutoff = new Date(now)
      cutoff.setFullYear(cutoff.getFullYear() - 5)
      break
  }

  const cutoffStr = cutoff.toISOString().split('T')[0]
  return prices.filter(p => p.date >= cutoffStr)
}
